import React, { useState } from 'react';
import { Link } from "gatsby";
import * as styles from './Mailchimp.module.css'; // Import the CSS module
import formSuccess from "../../assets/icon-form-success.png";
import Button from "../button/Button";

const Mailchimp = ({ success: externalSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    EMAIL: '',
    NAME: '', // Full name (optional) - will be split into FNAME and LNAME
    COMPANY: '', // Company (mandatory)
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // No longer needed since we're using iframe approach

  // Use external success prop if provided, otherwise use internal state
  const isSuccess = externalSuccess !== undefined ? externalSuccess : success;

  // Function to split name into first and last name
  const splitName = (fullName) => {
    if (!fullName || fullName.trim() === '') {
      return { FNAME: '', LNAME: '' };
    }
    
    const nameParts = fullName.trim().split(/\s+/);
    
    if (nameParts.length === 1) {
      // Only one name provided, put it in FNAME
      return { FNAME: nameParts[0], LNAME: '' };
    } else if (nameParts.length === 2) {
      // Two names, split evenly
      return { FNAME: nameParts[0], LNAME: nameParts[1] };
    } else {
      // Multiple names, put the larger section in FNAME
      const midPoint = Math.ceil(nameParts.length / 2);
      const firstName = nameParts.slice(0, midPoint).join(' ');
      const lastName = nameParts.slice(midPoint).join(' ');
      return { FNAME: firstName, LNAME: lastName };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.EMAIL) newErrors.EMAIL = 'Email is required.';
    if (!formData.COMPANY) newErrors.COMPANY = 'Company is required.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Validate form fields
    if (!validateForm()) {
      return;
    }

    // If validation passes, clear errors and proceed with form submission
    setErrors({});

    // Split the name into first and last name
    const { FNAME, LNAME } = splitName(formData.NAME);
    
    // Create a hidden iframe for form submission
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.name = 'mailchimp-iframe';
    document.body.appendChild(iframe);

    // Create a form element that targets the iframe
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://ustwo.us10.list-manage.com/subscribe/post?u=7f1269c0305abed7c91a24b97&id=ac9d3abe0a';
    form.target = 'mailchimp-iframe';
    form.style.display = 'none';

    // Add all form fields
    const fieldsToSubmit = {
      EMAIL: formData.EMAIL,
      FNAME: FNAME,
      LNAME: LNAME,
      MMERGE3: formData.COMPANY  // Company field maps to MMERGE3 in Mailchimp
    };
    
    Object.entries(fieldsToSubmit).forEach(([field, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field;
      input.value = value || '';
      form.appendChild(input);
    });

    // Add the email opt-in (always true since user consents by submitting)
    const optInInput = document.createElement('input');
    optInInput.type = 'hidden';
    optInInput.name = 'EMAIL_OPT_IN';
    optInInput.value = 'Y';
    form.appendChild(optInInput);

    // Hidden field required by Mailchimp to prevent bots
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'text';
    hiddenInput.name = 'b_7f1269c0305abed7c91a24b97_ac9d3abe0a';
    hiddenInput.tabIndex = '-1';
    hiddenInput.value = '';
    form.appendChild(hiddenInput);

    // Listen for iframe load to detect completion
    iframe.onload = () => {
      // Form submission completed - show success message
      setSuccess(true);
      
      // Track successful newsletter signup
      if (typeof window !== 'undefined' && window.rybbit) {
        window.rybbit.event("Newsletter Signup");
      }
      
      // Clean up iframe and form after a delay
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        if (document.body.contains(form)) {
          document.body.removeChild(form);
        }
      }, 2000);
    };

    // Append form to the document body and submit it
    document.body.appendChild(form);
    form.submit();
  };

  if (isSuccess) {
    return (
      <div className={styles.successMessage}>
        <img src={formSuccess} alt="" className={styles.iconTick} />
        <p className="textAlignCenter">Thank you for subscribing!</p>
        <p className="textAlignCenter textMarginBottomLarge">Look out for updates, insights, and all our latest news.</p>
        {onClose && (
          <Button 
            variant="pill"
            size="medium"
            onClick={onClose}
            className={styles.closeButton}
          >
            Close
          </Button>
        )}
      </div>
    );
  }

  return (
    <div id="mc_embed_shell" className={styles.mcEmbedShell}>
      <div id="mc_embed_signup" className={styles.mcEmbedSignup}>
        <h2>Newsletter Signup</h2>
        <form 
          onSubmit={handleFormSubmit} 
          className={`${styles.validate} validate`} 
          noValidate
          aria-label="Newsletter signup form"
          role="form"
        >
          {/* Error Summary - appears at top for screen readers */}
          {Object.keys(errors).length > 0 && (
            <div className={styles.errorSummary} role="alert" aria-live="polite">
              <ul>
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field} className="smallerBodyText noMargin">
                    {message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div id="mc_embed_signup_scroll" className={styles.mcEmbedSignupScroll}>
            <div className={styles.mcFieldGroup}>
              <label htmlFor="mce-EMAIL" className="h4">
                Email Address <span className={styles.asterisk} aria-label="required">*</span>
              </label>
              <input
                type="email"
                name="EMAIL"
                id="mce-EMAIL"
                required
                value={formData.EMAIL}
                onChange={handleInputChange}
                className={`${styles.input} ${errors.EMAIL ? styles.inputError : ''}`}
                aria-describedby={errors.EMAIL ? `mce-EMAIL-error` : undefined}
                aria-invalid={!!errors.EMAIL}
              />
            </div>
            <div className={styles.nameCompanyContainer}>
              <div className={styles.mcFieldGroup}>
                <label htmlFor="mce-NAME" className="h4">Name</label>
                <input
                  type="text"
                  name="NAME"
                  id="mce-NAME"
                  value={formData.NAME}
                  onChange={handleInputChange}
                  className={styles.input}
                />
              </div>
              <div className={styles.mcFieldGroup}>
                <label htmlFor="mce-COMPANY" className="h4">Company <span className={styles.asterisk} aria-label="required">*</span></label>
                <input
                  type="text"
                  name="COMPANY"
                  id="mce-COMPANY"
                  required
                  value={formData.COMPANY}
                  onChange={handleInputChange}
                  className={`${styles.input} ${errors.COMPANY ? styles.inputError : ''}`}
                  aria-describedby={errors.COMPANY ? `mce-COMPANY-error` : undefined}
                  aria-invalid={!!errors.COMPANY}
                />
              </div>
            </div>
            <div id="mergeRow-gdpr" className={`${styles.mergeRow} ${styles.gdprMergeRow} ${styles.contentGdprBlock} ${styles.mcFieldGroup}`}>
              <div className={styles.contentGdprLegal}>
                <p className="smallerBodyText colorDarkGrey">
                  By submitting this form, you agree to receive marketing updates from us. You can unsubscribe at any time using the link included in our emails. We use Mailchimp to handle your <a href="https://mailchimp.com/legal/terms" className={styles.gdprLink}>data securely</a>.
                </p>
              </div>
            </div>
            <div id="mce-responses" className={styles.clear}>
              {errors.form && <div className={styles.errorMessage}>{errors.form}</div>}
            </div>
            <div aria-hidden="true" style={{ position: 'absolute', left: '-5000px' }}>
              <input type="text" name="b_7f1269c0305abed7c91a24b97_ac9d3abe0a" tabIndex="-1" value="" readOnly />
            </div>
            <div className={styles.clear}>
              <Button 
                variant="pill"
                size="medium"
                onClick={handleFormSubmit}
                className={styles.submitButton}
              >
                Submit
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Mailchimp;
